/* eslint-disable @typescript-eslint/no-shadow */
import FormProvider from '@/commons/hook-form/FormProvider';
import { useCompanyCategory } from '@/hooks/useCompanyCategory';
import { translates } from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import CheckoutBillingInfo from '../../components/checkout/CheckoutBillingInfo';
import CheckoutPaymentMethods from '../../components/checkout/CheckoutPaymentMethods';
import CheckoutSummary from '../../components/checkout/CheckoutSummary';
import { Button } from '@PorkyCol/button';

const PAYMENT_OPTIONS: any[] = [
  {
    value: 'cash',
    title: translates.cash,
    description: 'Pago con Efectivo',
    icons: ['/assets/icons/payments/ic_paypal.svg'],
  },
  {
    value: 'card',
    title: translates.creditCard,
    description: 'Tarjeta visa, mastercard, american express',
    icons: ['/assets/icons/payments/ic_mastercard.svg', '/assets/icons/payments/ic_visa.svg'],
  },
  {
    value: 'bank',
    title: translates.transference,
    description: 'Pago con transferencia.',
    icons: [],
  },
];

const CARDS_OPTIONS: any[] = [
  { value: 'ViSa1', label: '**** **** **** 1212 - Jimmy Holland' },
  { value: 'ViSa2', label: '**** **** **** 2424 - Shawn Stokes' },
  { value: 'MasterCard', label: '**** **** **** 4545 - Cole Armstrong' },
];

type Props = {
  total: any;
  invoices: any;
  billingInfo: any;
  onSave: UseMutateFunction<AxiosResponse<any, any>, unknown, Partial<any>, unknown>;
  onEdit: UseMutateFunction<AxiosResponse<any, any>, unknown, Partial<any>, unknown>;
};

type FormValuesProps = {
  tips: number;
  bank: any;
  cash: any;
  card: any;
};

export default function CheckoutPaymentForm({
  invoices,
  billingInfo,
  onSave,
  onEdit,
  total,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { isFoodCompany } = useCompanyCategory();

  const PaymentSchema = useMemo(
    () =>
      Yup.object()
        .shape({
          bank: Yup.object()
            .shape({
              status: Yup.boolean().required().default(false),
              value: Yup.number().when('status', {
                is: true,
                then: Yup.number()
                  .required('Este campo es requerido')
                  .min(1, 'Ingrese un valor mayor')
                  .max(invoices?.total ?? total),
                otherwise: Yup.number().optional(),
              }),
              transaction: Yup.string().when('status', {
                is: true,
                then: Yup.string().required('Este campo es requerido'),
                otherwise: Yup.string().optional(),
              }),
            })
            .optional(),
          cash: Yup.object()
            .shape({
              status: Yup.boolean().required().default(false),
              value: Yup.number().when('status', {
                is: true,
                then: Yup.number()
                  .required('Este campo es requerido')
                  .min(1, 'Ingrese un valor mayor'),
                otherwise: Yup.number().optional(),
              }),
              transaction: Yup.string().optional(),
            })
            .optional(),
          card: Yup.object()
            .shape({
              status: Yup.boolean().required().default(false),
              value: Yup.number().when('status', {
                is: true,
                then: Yup.number()
                  .required('Este campo es requerido')
                  .min(1, 'Ingrese un valor mayor')
                  .max(invoices?.total ?? total),
                otherwise: Yup.number().optional(),
              }),
              transaction: Yup.string().when('status', {
                is: true,
                then: Yup.string().required('Este campo es requerido'),
                otherwise: Yup.string().optional(),
              }),
            })
            .optional(),
          tips: Yup.number().optional(),
        })
        .test('algunOptional', 'Que exista algun tipo', (values) => {
          const { bank, card, cash } = values;

          if (!bank.status && !card.status && !cash.status) {
            return false;
          }

          return true;
        }),
    [invoices?.total, total]
  );



  const defaultValues = {
    bank: {
      status: false,
    },
    card: {
      status: false,
    },
    tips: invoices.tips,
    cash: {
      status: false,
    },
  }
  
  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(PaymentSchema),
    defaultValues
  });

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid },
  } = methods;

  const onSubmit = useCallback(
    async (values: any) => {
      try {
        const { tips, ...rest } = values;
        const formatted = Object.keys(rest)
          .filter((key) => values[key].status)
          .map((item) => ({ type: item, payValue: values[item].value }));
        const totalMoney = formatted.reduce((stack, value) => stack + value.payValue, 0);

        if (totalMoney < invoices.total) {
          enqueueSnackbar('Dinero Insuficiente', { variant: 'error' });
          throw Error('Dinero Insuficiente');
        }
        await onSave({
          tips,
          payments: formatted,
        });
        // navigate(PATH_DASHBOARD.orders.facture(invoices.id));
      } catch (error) {
        console.error(error);
      }
    },
    [enqueueSnackbar, invoices.total, onSave]
  );
  const onSubmitTip = useCallback(
    async (tips: number) => {
      try {
        await onEdit({
          tips,
        });
        // navigate(PATH_DASHBOARD.orders.facture(invoices.id));
      } catch (error) {
        console.error(error);
      }
    },
    [onEdit]
  );
  const exchange = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tips, ...rest } = watch();
    const payAmount = Object.values(rest).reduce(
      (prev, current) => prev + Number(current?.value || '0'),
      0
    );

    return (payAmount ?? 0) - (invoices?.total ?? 0);
  };

  useEffect(() => {
    if (!isFoodCompany && invoices.tips) {
      onSubmitTip(0)
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFoodCompany]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <CheckoutPaymentMethods
            cardOptions={CARDS_OPTIONS}
            paymentOptions={PAYMENT_OPTIONS}
            sx={{ my: 3 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CheckoutBillingInfo billing={billingInfo} />

          <CheckoutSummary
            enableEdit
            invoices={invoices}
            exchange={exchange()}
            onEdit={onSubmitTip}
          />
          <Button
            fullWidth
            disabled={!isValid}
            color="success"
            type="submit"
            variant="solid"
            loading={isSubmitting}
          >
            Completar Orden
          </Button>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
